/* eslint-disable react/no-array-index-key */
import { ChevronDownIcon, CircleInfoIcon, PenIcon, PlusIcon, TrashIcon } from "@xola/icons";
import { flash, FormGroup, getSymbol, Label, SubmitButton, Switch, Textarea, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, getDuration, InputField, SelectField, SwitchField } from "common";
import dayjs from "dayjs";
import { FieldArray } from "formik";
import { orderBy } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    Channel,
    ChannelEmailDefaultOffsetMap,
    ChannelType,
    GratuityAlgorithm,
    GratuityPreference,
    GratuityPreferenceOption,
    GratuitySource,
    NotificationPreference,
    NotificationPreferenceType,
    ProductObject,
    Seller,
} from "types";
import { ProductSelectorModal } from "../Common/ProductSelectorModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { SelectedProductList } from "../Common/SelectedProductList";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { api } from "@/api/api";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { defaultExperiences } from "@/models/SelectedExperiences";
import { getSelectedExperiences, getSelectionState } from "@/models/SelectionState";

export const GratuitySettings = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const history = useHistory();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const { mutateAsync: updateGratuityPreferences, isLoading: isLoadingGratuityPreferences } = useMutation(
        async (preference: GratuityPreference) => {
            return api.post(`/sellers/${seller.id}/preferences/gratuity`, preference);
        },
    );

    const { mutateAsync: updateChannelPreferences, isLoading: isLoadingChannelPreferences } = useMutation(
        async (channels: Channel[] | undefined) => {
            if (channels) {
                return api.put(`/sellers/${seller.id}/preferences/notifications/gratuity_reminder_traveler`, {
                    channels,
                    type: NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER,
                });
            }
        },
    );

    const handleSubmit = async (values: Seller) => {
        const gratuityNotifications = values.preferences.notifications.find(
            (notification: NotificationPreference) =>
                notification.type === NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER,
        );
        await Promise.all([
            updateGratuityPreferences(values.preferences.gratuity),
            updateChannelPreferences(gratuityNotifications?.channels),
        ]);

        invalidate("sellers", seller.id);
        flash.success("Your settings are saved");
    };

    return (
        <XolaFormik validateOnBlur validateOnChange={false} initialValues={seller} onSubmit={handleSubmit}>
            {({ handleSubmit, setFieldValue, values, handleReset }) => {
                const gratuityNotifications = values.preferences.notifications.find(
                    (notification: NotificationPreference) =>
                        notification.type === NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER,
                );
                const indexOfReminderTraveler = values.preferences.notifications.findIndex(
                    (notification: NotificationPreference) =>
                        notification.type === NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER,
                );
                const isEnabledGratuityReviewReminder = values.preferences.gratuity.addToReviewReminder;
                const enabledChannels = values?.preferences?.communicationChannels?.enabledChannels ?? [];
                const isEnabledNotificationPreference = gratuityNotifications?.channels.some(
                    (channel: Channel) => channel.enabled && enabledChannels.includes(channel.type),
                );
                const name = `preferences.notifications[${indexOfReminderTraveler}].channels`;
                const isEnabledForBackOffice = values.preferences?.gratuity?.enabledSources?.includes(
                    GratuitySource.OFFICE,
                );
                const isEnabledForCheckout = values.preferences?.gratuity?.enabledSources?.includes(
                    GratuitySource.CHECKOUT,
                );
                const inPurchaseToolTip =
                    "If gratuity is already collected as part of the purchase workflow, customers will not be prompted for gratuity again.";

                return (
                    <ContentLayout isWide className="space-y-4 divide-y divide-gray-lighter">
                        <div>
                            <h2 className="mt-4">Gratuity</h2>
                            <ContentLayout.Section
                                title="Notifications"
                                description="Choose how your customers will receive the gratuity notification"
                            >
                                <FieldArray
                                    name={name}
                                    render={(arrayHelpers) => (
                                        <div className="space-y-4">
                                            {orderBy(gratuityNotifications?.channels, ["type"], "desc")?.map(
                                                (channel: Channel) => {
                                                    const index = gratuityNotifications?.channels.findIndex(
                                                        (item: Channel) => item.type === channel.type,
                                                    );
                                                    if (enabledChannels.includes(channel.type)) {
                                                        const offset = channel.offset
                                                            ? channel.offset
                                                            : ChannelEmailDefaultOffsetMap[
                                                                  NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER
                                                              ];

                                                        const offsetMinutes = dayjs.duration(offset).asMinutes();
                                                        const { value, unit } = getDuration(offsetMinutes);

                                                        return (
                                                            <NotificationSwitchGroup
                                                                key={index}
                                                                name={`preferences.notifications[${indexOfReminderTraveler}].channels[${index}]`}
                                                                isChecked={channel.enabled}
                                                                label={communicationChannelCopy[channel.type]}
                                                                notificationLabel={`${value} ${unit}s`}
                                                                notificationDescription="after event completion"
                                                                onChange={(value: boolean) => {
                                                                    arrayHelpers.replace(index!, {
                                                                        ...channel,
                                                                        enabled: value,
                                                                    });
                                                                }}
                                                                onClickNotification={() => {
                                                                    history.push("/settings/notifications/gratuity");
                                                                }}
                                                            />
                                                        );
                                                    }

                                                    return null;
                                                },
                                            )}
                                        </div>
                                    )}
                                />
                                <FormField name="addToReviewReminder" className="mt-4">
                                    <SwitchField
                                        className="!items-start leading-p1"
                                        isChecked={values.preferences.gratuity.addToReviewReminder}
                                        onChange={(value: boolean) => {
                                            setFieldValue("preferences.gratuity.addToReviewReminder", value);
                                        }}
                                    >
                                        <Switch.Label direction="left" className="font-bold" size="medium">
                                            Show in Customer Review Email
                                        </Switch.Label>
                                        <div className="text-gray-darker">
                                            The gratuity section prompts guest to consider leaving a gratuity
                                        </div>
                                    </SwitchField>
                                </FormField>
                            </ContentLayout.Section>
                        </div>
                        {isEnabledGratuityReviewReminder || !!isEnabledNotificationPreference ? (
                            <ContentLayout.Section
                                title="Settings"
                                description="Customize what your customers see when presented with gratuity"
                            >
                                <div className="space-y-8">
                                    <FormGroup className="space-y-2">
                                        <Label>Preset Amounts</Label>
                                        <FieldArray
                                            name="preferences.gratuity.options"
                                            render={(arrayHelpers) => (
                                                <div>
                                                    {values.preferences.gratuity.options.map(
                                                        (option: GratuityPreferenceOption, index: number) => (
                                                            <GratuityPresetAmount
                                                                key={index}
                                                                amountName={`preferences.gratuity.options[${index}].amount`}
                                                                algorithmName={`preferences.gratuity.options[${index}].algorithm`}
                                                                option={option}
                                                                currency={seller.currency}
                                                                onDelete={() => arrayHelpers.remove(index)}
                                                            />
                                                        ),
                                                    )}
                                                    {values.preferences.gratuity.options.length < 3 ? (
                                                        <SecondaryButton
                                                            size="small"
                                                            onClick={() => {
                                                                arrayHelpers.push({
                                                                    amount: "",
                                                                    algorithm: GratuityAlgorithm.ABSOLUTE,
                                                                });
                                                            }}
                                                        >
                                                            <div className="flex items-center space-x-2">
                                                                <PlusIcon />
                                                                <h6>Add Preset Amount</h6>
                                                            </div>
                                                        </SecondaryButton>
                                                    ) : null}
                                                </div>
                                            )}
                                        />
                                    </FormGroup>
                                    <GratuityTextArea
                                        label="Custom message"
                                        tooltipMessage="Custom messages are displayed in the email sent to your customers requesting gratuity and in the payment page"
                                        name="preferences.gratuity.message"
                                    />
                                    <GratuityTextArea
                                        label="Guest instructions"
                                        tooltipMessage="This is shown next to the “Leave a note” field just before your guest pays the gratuity. If you’d like to ask them a specific question, this is the perfect place to do so (e.g. which guide served you)."
                                        name="preferences.gratuity.commentLabel"
                                    />
                                    <div className="space-y-2">
                                        {/* TODO: Update to use SelectedProductListInput */}
                                        <div className="flex items-center justify-between">
                                            <Label>Applies to</Label>

                                            <SecondaryButton
                                                data-testid="gratuity-settings-applies-to-button"
                                                size="small"
                                                onClick={() => {
                                                    setIsOpen(true);
                                                }}
                                            >
                                                <PenIcon />
                                            </SecondaryButton>
                                        </div>
                                        <ProductSelectorModal
                                            isOpen={isOpen}
                                            value={getSelectionState(
                                                values.preferences.gratuity.experiences ?? defaultExperiences,
                                            )}
                                            objects={[ProductObject.EXPERIENCE]}
                                            onClose={() => {
                                                setIsOpen(false);
                                            }}
                                            onConfirm={(autoAccept) => {
                                                setFieldValue(
                                                    "preferences.gratuity.experiences",
                                                    getSelectedExperiences(autoAccept),
                                                );
                                            }}
                                        />
                                        <SelectedProductList
                                            selectedProducts={getSelectionState(
                                                values.preferences.gratuity.experiences ?? defaultExperiences,
                                            )}
                                        />
                                        <div className="flex flex-col gap-3 pt-4">
                                            <SwitchField
                                                isChecked={isEnabledForBackOffice ?? false}
                                                onChange={(value: boolean) => {
                                                    if (value) {
                                                        if (!isEnabledForBackOffice) {
                                                            setFieldValue("preferences.gratuity.enabledSources", [
                                                                ...values.preferences.gratuity.enabledSources,
                                                                GratuitySource.OFFICE,
                                                            ]);
                                                        }
                                                    } else {
                                                        // remove 'office' from enabledSources
                                                        setFieldValue(
                                                            "preferences.gratuity.enabledSources",
                                                            values.preferences.gratuity.enabledSources?.filter(
                                                                (source) => source !== GratuitySource.OFFICE,
                                                            ),
                                                        );
                                                    }
                                                }}
                                            >
                                                <span>Collect in back office purchase workflow</span>
                                                <Tooltip content={inPurchaseToolTip} placement="right">
                                                    <CircleInfoIcon color="text-danger" />
                                                </Tooltip>
                                            </SwitchField>
                                            <SwitchField
                                                isChecked={isEnabledForCheckout ?? false}
                                                onChange={(value: boolean) => {
                                                    if (value) {
                                                        if (!isEnabledForCheckout) {
                                                            setFieldValue("preferences.gratuity.enabledSources", [
                                                                ...values.preferences.gratuity.enabledSources,
                                                                GratuitySource.CHECKOUT,
                                                            ]);
                                                        }
                                                    } else {
                                                        setFieldValue(
                                                            "preferences.gratuity.enabledSources",
                                                            values.preferences.gratuity.enabledSources?.filter(
                                                                (source) => source !== GratuitySource.CHECKOUT,
                                                            ),
                                                        );
                                                    }
                                                }}
                                            >
                                                <span>Collect in checkout purchase workflow</span>
                                                <Tooltip content={inPurchaseToolTip} placement="right">
                                                    <CircleInfoIcon color="text-danger" />
                                                </Tooltip>
                                            </SwitchField>
                                        </div>
                                    </div>
                                </div>
                            </ContentLayout.Section>
                        ) : null}
                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton
                                isLoading={isLoadingGratuityPreferences || isLoadingChannelPreferences}
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>

                            <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

interface NotificationSwitchGroupProps {
    name: string;
    isChecked: boolean;
    onChange: (value: boolean) => void;
    label: string;
    notificationLabel: string;
    notificationDescription: string;
    onClickNotification: () => void;
}

const NotificationSwitchGroup = ({
    name,
    isChecked,
    onChange,
    label,
    notificationLabel,
    notificationDescription,
    onClickNotification,
}: NotificationSwitchGroupProps) => {
    const handleNotificationLabelCLick = (event: any) => {
        // Avoid changing switch value after clicking on the time field.
        event.stopPropagation();
        onClickNotification();
    };

    return (
        <FormField name={name}>
            <SwitchField isChecked={isChecked} className="!items-start leading-p1" onChange={onChange}>
                <Switch.Label direction="left" className="font-bold" size="medium">
                    {label}
                </Switch.Label>
                <div className="mt-1 flex cursor-pointer items-center" onClick={handleNotificationLabelCLick}>
                    <h5 className="font-bold underline">{notificationLabel}</h5>
                    <ChevronDownIcon size="small" className="ml-1 mr-2" />
                    <h5 className="text-gray-darker">{notificationDescription}</h5>
                </div>
            </SwitchField>
        </FormField>
    );
};

interface GratuityTextAreaProps {
    label: string;
    tooltipMessage: string;
    name: string;
}

const GratuityTextArea = ({ label, tooltipMessage, name }: GratuityTextAreaProps) => (
    <FormField name={name} label={label} tooltip={tooltipMessage}>
        <InputField as={Textarea} className="h-28" />
    </FormField>
);

interface GratuityPresetAmountProps {
    option: GratuityPreferenceOption;
    onDelete: () => void;
    currency: string;
    amountName: string;
    algorithmName: string;
}

const GratuityPresetAmount = ({ option, onDelete, currency, amountName, algorithmName }: GratuityPresetAmountProps) => {
    const currencyLength = getSymbol(currency).length;

    return (
        <div
            className="flex w-full items-center justify-between space-y-2"
            data-testid="gratuity-settings-preset-section"
        >
            <div className="flex items-center">
                <div className="relative">
                    <FormField name={amountName} className="max-w-[125px]">
                        <InputField type="text" className="block w-30 text-right" />
                    </FormField>

                    <div className="absolute inset-y-0 left-0 flex items-center">
                        <FormField name={algorithmName}>
                            <SelectField
                                className={clsx(
                                    "h-10 !rounded-none !border-transparent !border-r-gray-light bg-transparent py-0 hover:bg-transparent",
                                    currencyLength > 2 && option.algorithm === GratuityAlgorithm.ABSOLUTE
                                        ? "w-20"
                                        : "w-15",
                                )}
                            >
                                <option className="no-translate" value={GratuityAlgorithm.ABSOLUTE}>
                                    {getSymbol(currency)}
                                </option>
                                <option className="no-translate" value={GratuityAlgorithm.PERCENT}>
                                    %
                                </option>
                            </SelectField>
                        </FormField>
                    </div>
                </div>
                <div className="ml-2">{option.algorithm === GratuityAlgorithm.PERCENT ? `of Booking value` : null}</div>
            </div>
            <SecondaryButton size="small" onClick={onDelete}>
                <TrashIcon />
            </SecondaryButton>
        </div>
    );
};

const communicationChannelCopy = {
    [ChannelType.EMAIL]: "Send Email",
    [ChannelType.SMS]: "Send SMS Text Message",
};
